import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Upload, CheckCircle, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import { pageCache } from '../utils/pageCache';
import { describeUpload } from '../utils/uploadSummary';
import TimeMachineRevealModal, { useTimeMachineReveal } from '../components/TimeMachineRevealModal';
import { PRO_PRICE_LABEL } from '../pricing';
import UploadKindDialog from '../components/UploadKindDialog';
import { InspectResult, UploadKind, importedLine } from '../utils/uploadKind';
import { fmtCount } from '../utils/formatters';
import InvoiceEmailCard from '../components/costs/InvoiceEmailCard';

// Supplier invoices and the bills list moved to the Costs page
// (components/costs/SupplierInvoices, components/costs/BillsList).
export default function UploadPage() {
  const [uploads, setUploads] = useState<any[]>([]);
  // "Had you been on ClearPath…" — fires after a real CSV lands, never for sample data.
  const tmReveal = useTimeMachineReveal();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pending, setPending] = useState<{ file: File; inspect: InspectResult } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const isPro = user?.plan === 'pro';

  const fetchUploads = () => {
    axios.get('/api/uploads')
      .then((res) => setUploads(res.data.uploads))
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUploads(); }, []);

  // Every CSV is inspected first and imported only after the owner confirms
  // what it is: register sales, payments received, or bills paid. The three
  // never mix (spec 2026-09-24-three-streams).
  const handleUpload = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    if (file.size > 10_000_000) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post('/api/upload/inspect', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPending({ file, inspect: res.data });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please check your CSV format.');
    } finally {
      setUploading(false);
    }
  };

  const confirmUpload = async (kind: UploadKind) => {
    if (!pending) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', pending.file);
    formData.append('kind', kind);
    try {
      const res = await axios.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPending(null);
      pageCache.clear('/api/'); // every stream's pages recompute
      fetchUploads();
      if (kind !== 'sales') {
        // Bills live on the Costs page now (the list used to sit on this page).
        toast.success(importedLine(kind, res.data.records), {
          action: { label: 'Open', onClick: () => navigate(kind === 'payments' ? '/payments' : '/costs') },
        });
        return;
      }
      const { headline, detail, notice } = describeUpload(res.data);
      toast.success(headline, detail ? { description: detail } : undefined);
      if (notice) toast.warning(notice, { duration: 12000 });
      // No reveal on data the Time Machine will not judge (not item sales):
      // it would poll for twenty seconds and find nothing.
      if (res.data.records > 0 && !notice) tmReveal.reveal();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please check your CSV format.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUpload = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`/api/uploads/${deleteTarget.upload_id}`);
      pageCache.clear('/api/');
      setUploads((prev) => prev.filter((u) => u.upload_id !== deleteTarget.upload_id));
      toast.success(`Upload deleted — ${fmtCount(res.data.records_deleted)} transactions removed`);
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete upload');
    } finally {
      setDeleting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const handleLoadSample = async () => {
    setUploading(true);
    try {
      await axios.post('/api/load-sample-data');
      pageCache.clear('/api/');
      toast.success('Sample data loaded successfully');
      fetchUploads();
    } catch (e: any) {
      // Show the server's reason (a load already running, real data in the
      // way) — "Failed" alone sent people into retry loops.
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to load sample data');
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <TimeMachineRevealModal state={tmReveal.state} report={tmReveal.report} onClose={tmReveal.close} />
      {pending && (
        <UploadKindDialog
          fileName={pending.file.name}
          inspect={pending.inspect}
          busy={uploading}
          onConfirm={confirmUpload}
          onCancel={() => setPending(null)}
        />
      )}
      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-[18px] shadow-xl border border-[#E6E2DE] w-full max-w-md"
          >
            <div className="p-6 border-b border-[#E6E2DE]">
              <h3 className="font-serif text-lg text-[#2D2A2E]">Delete this upload?</h3>
            </div>
            <div className="p-6">
              <p className="font-sans text-sm text-[#2D2A2E]">
                <span className="font-medium">{deleteTarget.file_name}</span> — {fmtCount(deleteTarget.records_imported)} records imported.
              </p>
              <p className="font-sans text-sm text-[#6B6670] mt-2">
                This permanently removes those transactions from your analytics. This cannot be undone.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 bg-white border border-[#E6E2DE] text-[#2D2A2E] hover:bg-[#FAF9F7] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUpload}
                disabled={deleting}
                className="flex-1 bg-[#B54A4A] text-white hover:bg-[#9E3E3E] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">Upload Data</h1>
      </div>

      {/* Paywall for non-Pro users */}
      {!isPro ? (
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-10 text-center shadow-card">
          <div className="w-16 h-16 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center mx-auto mb-4">
            <Lock size={28} strokeWidth={1.5} className="text-[#4A2D5E]" />
          </div>
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">Pro Feature</h3>
          <p className="font-sans text-sm text-[#6B6670] max-w-md mx-auto mb-6">
            Upload your own POS data to unlock personalized insights, product analysis, and AI recommendations — all tailored to your business.
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-8 py-3 transition-colors"
          >
            Upgrade to Pro — {PRO_PRICE_LABEL}/mo
          </button>
          <div className="mt-4">
            <button
              onClick={handleLoadSample}
              disabled={uploading}
              className="font-sans text-sm text-[#4A2D5E] hover:underline disabled:opacity-50"
            >
              Or explore with sample data for free
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`bg-white border-2 border-dashed rounded-[14px] p-12 text-center cursor-pointer transition-all ${
              dragOver ? 'border-[#4A2D5E] bg-[#F0EBF5]' : 'border-[#E6E2DE] hover:border-[#4A2D5E]/40'
            }`}
          >
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
            {uploading ? (
              <LoadingScreen fullScreen={false} compact label="Processing your data…" />
            ) : (
              <>
                <Upload size={40} strokeWidth={1.5} className="text-[#4A2D5E]/40 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-[#2D2A2E] mb-1">Drop your CSV here</h3>
                <p className="font-sans text-sm text-[#6B6670] mb-4">or click to browse files</p>
                <p className="font-sans text-xs text-[#6B6670]">
                  Works with Square, Clover, Shopify, Toast — any POS that exports CSV (max 10MB)
                </p>
              </>
            )}
          </div>

          {/* Sample data option */}
          <div className="mt-4 text-center">
            <button
              onClick={handleLoadSample}
              disabled={uploading}
              className="font-sans text-sm text-[#4A2D5E] hover:underline disabled:opacity-50"
            >
              Or load sample data to explore ClearPath
            </button>
          </div>

          <div className="mt-8">
            <InvoiceEmailCard />
          </div>
        </>
      )}

      {/* Upload history */}
      <div className="mt-8">
        <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Upload History</h3>
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-[#F2F0ED] rounded-[14px] h-16" />
            ))}
          </div>
        ) : uploads.length === 0 ? (
          <p className="font-sans text-sm text-[#6B6670]">No uploads yet. Upload a CSV or load sample data to get started.</p>
        ) : (
          <div className="space-y-3">
            {uploads.map((u) => (
              <div key={u.upload_id} className="bg-white border border-[#E6E2DE] rounded-[14px] p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#2E6B4F]/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={20} strokeWidth={1.5} className="text-[#2E6B4F]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-sans text-sm font-medium text-[#2D2A2E] truncate">{u.file_name}</div>
                  <div className="font-sans text-xs text-[#6B6670]">
                    {fmtCount(u.records_imported)} records{u.records_skipped > 0 ? ` (${fmtCount(u.records_skipped)} skipped)` : ''} · Uploaded {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => setDeleteTarget(u)}
                  className="text-[#6B6670] hover:text-[#B54A4A] transition-colors p-1 flex-shrink-0"
                  aria-label={`Delete ${u.file_name}`}
                >
                  <Trash2 size={18} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
